import { useState, useEffect, useCallback } from 'react'
import styled from 'styled-components'
import { useSearchParams } from 'react-router-dom'
import { api } from '../../../services/api'
import { useRateLimit } from '../../../hooks/useRateLimit'
import { RateLimitIndicator } from '../../../components/RateLimitIndicator'
import {
  Container,
  InputDiv,
  RadioInputDiv,
  Card,
  Result,
  MathDiv,
  ActionButtonsRow,
  Select,
  CopyHTMLButton,
  MobileOnlyDiv,
  DesktopOnlyDiv,
  PageContainer,
  PageHeader,
  Badge,
  InfoCard,
  BackButton,
  RadioWrapper,
} from '../../../styles/global'
import { BlockMath } from 'react-katex'
import 'katex/dist/katex.min.css'
import { toast } from 'react-toastify'
import { ValidatedInput } from '../../../components/ValidatedInput'
import { Input } from '../../../components/Input'
import { Spinner } from '../../../components/Spinner'
import { HistoryNavigation } from '../../../components/HistoryNavigation'
import { LoadingOverlay } from '../../../components/LoadingOverlay'
import { FiArrowLeft, FiInfo } from 'react-icons/fi'

import { copyResultsToClipboard } from '../../../utils/copyResultsToClipboard'
import { roundToDecimals } from '../../../utils/numberFormat'

const RamosInput = styled(Input)`
  margin-left: 10px;

  @media (max-width: 720px) {
    margin-left: 0 !important;
  }
`

export function ConcreteShear() {
  const { canCalculate, updateFromHeaders, handleRateLimitError, fetchRateLimitStatus } = useRateLimit()

  const [searchParams] = useSearchParams()
  const [b, setB] = useState('')
  const [d, setD] = useState('')
  const [thetaDeg, setThetaDeg] = useState('45')
  const [alphaDeg, setAlphaDeg] = useState('90')
  const [vsk, setVsk] = useState('')
  const [gammaf, setGammaf] = useState(1.4)
  const [fck, setFck] = useState(20)
  const [fyk, setFyk] = useState(500)
  const [isFormValid, setIsFormValid] = useState(false)
  const [model, setModel] = useState('1')
  const [data, setData] = useState({})
  const [result, setResult] = useState('')
  const [selectedRebar, setSelectedRebar] = useState('Ø5.0')
  const [sideNumber, setSideNumber] = useState('2')
  const [touched, setTouched] = useState({})
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isRecalculating, setIsRecalculating] = useState(false)
  const [history, setHistory] = useState([])
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1)
  const [isThetaDisabled, setIsThetaDisabled] = useState(true)

  const HISTORY_KEY = 'concrete-shear-history'
  const MAX_HISTORY = 10

  useEffect(() => {
    const savedHistory = localStorage.getItem(HISTORY_KEY)
    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory)
        setHistory(parsed)
      } catch (error) {
        console.error('Erro ao carregar histórico:', error)
      }
    }
  }, [])

  const saveToHistory = useCallback((calculationData) => {
    const newEntry = {
      ...calculationData,
      timestamp: new Date().toISOString(),
      id: Date.now(),
    }

    setHistory((prevHistory) => {
      const newHistory = [newEntry, ...prevHistory].slice(0, MAX_HISTORY)
      localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory))
      return newHistory
    })
    setCurrentHistoryIndex(0)
  }, [])

  const loadFromHistory = useCallback(
    (index) => {
      if (index < 0 || index >= history.length) return

      const entry = history[index]
      setB(entry.b)
      setD(entry.d)
      setThetaDeg(entry.thetaDeg)
      setAlphaDeg(entry.alphaDeg)
      setVsk(entry.vsk)
      setGammaf(entry.gammaf || 1.4)
      setFck(entry.fck)
      setFyk(entry.fyk)
      setModel(entry.model)
      setResult(entry.result)
      setData(entry.data)
      setSelectedRebar(entry.selectedRebar || 'Ø5.0')
      setSideNumber(entry.sideNumber || '2')
      setIsThetaDisabled(entry.model === '1')
      setCurrentHistoryIndex(index)
    },
    [history]
  )

  const navigateHistory = useCallback(
    (direction) => {
      const newIndex = currentHistoryIndex + direction
      if (newIndex >= 0 && newIndex < history.length) {
        loadFromHistory(newIndex)
      }
    },
    [currentHistoryIndex, history.length, loadFromHistory]
  )

  const validateField = useCallback((name, value) => {
    if (value === '' || value === null || value === undefined) {
      return 'Campo obrigatório'
    }

    const numValue = Number(value)

    if (isNaN(numValue)) {
      return 'Valor deve ser um número'
    }

    if (numValue <= 0) {
      return 'Valor deve ser maior que 0'
    }

    return null
  }, [])

  useEffect(() => {
    const newErrors = {}

    newErrors.fck = validateField('fck', fck)
    newErrors.fyk = validateField('fyk', fyk)
    newErrors.b = validateField('b', b)
    newErrors.d = validateField('d', d)
    newErrors.vsk = validateField('vsk', vsk)
    newErrors.gammaf = validateField('gammaf', gammaf)
    newErrors.model = validateField('model', model)
    newErrors.thetaDeg = validateField('thetaDeg', thetaDeg)
    newErrors.alphaDeg = validateField('alphaDeg', alphaDeg)

    setErrors(newErrors)

    const hasErrors = Object.values(newErrors).some((error) => error !== null)
    setIsFormValid(!hasErrors)
  }, [fck, fyk, b, d, vsk, gammaf, model, thetaDeg, alphaDeg, validateField])

  const handleBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }))
  }

  useEffect(() => {
    const get = (k) => searchParams.get(k)

    const toNum = (v) => {
      if (v === null || v === '') return undefined
      const n = Number(String(v).replace(',', '.'))
      return Number.isFinite(n) ? n : undefined
    }

    const setIfNum = (param, setter) => {
      const n = toNum(get(param))
      if (n !== undefined) setter(n)
    }

    setIfNum('fck', setFck)
    setIfNum('fyk', setFyk)
    setIfNum('b', setB)
    setIfNum('d', setD)
  }, [searchParams])

  async function handleCalculateBeam(event) {
    event.preventDefault()

    const hasExistingResults = result !== ''

    setIsLoading(true)
    if (hasExistingResults) {
      setIsRecalculating(true)
    }

    try {
      const data = {
        b,
        d,
        vsd: vsk * gammaf,
        thetaDeg,
        alphaDeg,
        fck,
        fyk,
        model,
      }

      setData(data)

      const response = await api.post('/concrete/shear', data)
      setResult(response.data)

      if (response.rateLimit) {
        updateFromHeaders({
          'x-ratelimit-limit': response.rateLimit.limit.toString(),
          'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
          'x-ratelimit-reset': response.rateLimit.reset,
        })
      }

      await fetchRateLimitStatus()

      saveToHistory({
        b,
        d,
        vsk,
        gammaf,
        thetaDeg,
        alphaDeg,
        fck,
        fyk,
        model,
        data,
        result: response.data,
        selectedRebar,
        sideNumber,
      })

      toast.success('Cálculo realizado com sucesso!')
    } catch (error) {
      console.error('Erro ao calcular:', error)

      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }

      toast.error('Erro ao realizar o cálculo. Tente novamente.')
    } finally {
      setIsLoading(false)
      if (hasExistingResults) {
        setTimeout(() => setIsRecalculating(false), 500)
      }
    }
  }

  function handleSave() {
    const stateData = {
      b,
      d,
      thetaDeg,
      alphaDeg,
      vsk,
      gammaf,
      fck,
      fyk,
      isFormValid,
      model,
      data,
      result,
      selectedRebar,
      sideNumber,
    }
    const jsonString = JSON.stringify(stateData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'state.json'
    link.click()
    URL.revokeObjectURL(url)
    toast.success('Estado salvo com sucesso!')
  }

  function handleFileUpload(event) {
    const file = event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result)

        setB(jsonData.b)
        setD(jsonData.d)
        setThetaDeg(jsonData.thetaDeg)
        setAlphaDeg(jsonData.alphaDeg)
        setVsk(jsonData.vsk)
        setGammaf(jsonData.gammaf || 1.4)
        setFck(jsonData.fck)
        setFyk(jsonData.fyk)
        setIsFormValid(jsonData.isFormValid)
        setModel(jsonData.model)
        setData(jsonData.data)
        setResult(jsonData.result)
        setSelectedRebar(jsonData.selectedRebar)
        setSideNumber(jsonData.sideNumber)
        toast.success('Estado carregado com sucesso!')
      } catch (error) {
        console.error('Erro ao ler o arquivo JSON:', error)
        toast.error('Erro ao carregar o estado')
      }
    }
    reader.readAsText(file)
  }

  function handleCopyResults() {
    copyResultsToClipboard({
      result,
    })
  }

  return (
    <PageContainer>
      <PageHeader $gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
        <div className="header-content">
          <BackButton to="/concreto">
            <FiArrowLeft /> Voltar para Concreto
          </BackButton>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <a href="/concreto">Concreto</a>
            <span>/</span>
            <span>Cisalhamento</span>
          </div>
          <h1>✂️ Cisalhamento em Concreto Armado</h1>
          <p className="subtitle">
            Dimensionamento de armadura transversal para elementos de concreto armado submetidos ao esforço cortante
          </p>
          <div className="badges">
            <Badge>⭐ Popular</Badge>
            <Badge>📋 NBR 6118:2014</Badge>
            <Badge>🎯 Modelos I e II</Badge>
          </div>
        </div>
      </PageHeader>

      <InfoCard
        $background="linear-gradient(135deg, #f3f0ff 0%, #e9e0ff 100%)"
        $border="2px solid #d4c5f9"
        $primaryColor="#764ba2"
      >
        <div className="info-content">
          <div className="icon">
            <FiInfo />
          </div>
          <div className="text">
            <h3>Sobre esta Calculadora</h3>
            <p>
              Esta ferramenta realiza o dimensionamento de armadura transversal (estribos) para elementos de concreto
              armado submetidos ao esforço cortante, conforme a NBR 6118:2014.
            </p>
            <p>
              O cálculo permite a escolha entre o Modelo I (treliça clássica com θ = 45°) e o Modelo II (treliça
              generalizada com θ variável), fornecendo a área de aço por metro e o espaçamento adequado dos estribos.
            </p>
            <div className="norms">
              <span>NBR 6118:2014</span>
              <span>Modelo de Treliça</span>
            </div>
          </div>
        </div>
      </InfoCard>

      <Container>
        <Card>
          <RateLimitIndicator />

          <h2>Seção à cisalhamento</h2>
          {history.length > 0 && (
            <HistoryNavigation
              currentIndex={currentHistoryIndex}
              totalItems={history.length}
              onNavigate={navigateHistory}
              currentTimestamp={currentHistoryIndex >= 0 ? history[currentHistoryIndex]?.timestamp : null}
            />
          )}
          <form onSubmit={handleCalculateBeam}>
            <h3>Materiais</h3>
            <InputDiv>
              <span>Resistência Característica do Concreto (MPa)</span>
              <ValidatedInput
                name="fck"
                type="number"
                value={fck || ''}
                onChange={(event) => setFck(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Resistência Característica do Aço (MPa)</span>
              <ValidatedInput
                name="fyk"
                type="number"
                value={fyk || ''}
                onChange={(event) => setFyk(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Geometria</h3>
            <InputDiv>
              <span>Largura (cm)</span>
              <ValidatedInput
                name="b"
                type="number"
                value={b || ''}
                onChange={(event) => setB(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Altura útil (cm)</span>
              <ValidatedInput
                name="d"
                type="number"
                value={d || ''}
                onChange={(event) => setD(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Esforços</h3>
            <InputDiv>
              <span>Cortante Característico (kN)</span>
              <ValidatedInput
                name="vsk"
                type="number"
                value={vsk || ''}
                onChange={(event) => setVsk(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Coeficiente de ponderação γf</span>
              <ValidatedInput
                name="gammaf"
                type="number"
                value={gammaf || ''}
                onChange={(event) => setGammaf(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Considerações</h3>
            <RadioInputDiv>
              <span>Modelo de Cálculo</span>
              <div>
                <RadioWrapper>
                  <input
                    type="radio"
                    name="model"
                    id="model1"
                    value="1"
                    checked={model === '1'}
                    onChange={(e) => {
                      setModel(e.target.value)
                      setIsThetaDisabled(true)
                      setThetaDeg('45')
                    }}
                  />
                  <span>Modelo I</span>
                </RadioWrapper>
                <RadioWrapper>
                  <input
                    type="radio"
                    name="model"
                    id="model2"
                    value="2"
                    checked={model === '2'}
                    onChange={(e) => {
                      setModel(e.target.value)
                      setIsThetaDisabled(false)
                    }}
                  />
                  <span>Modelo II</span>
                </RadioWrapper>
              </div>
            </RadioInputDiv>
            <InputDiv>
              <span>Inclinação da biela (º)</span>
              <ValidatedInput
                name="thetaDeg"
                type="number"
                value={thetaDeg || ''}
                onChange={(event) => setThetaDeg(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading || isThetaDisabled}
              />
            </InputDiv>
            <InputDiv>
              <span>Inclinação do estribo (º)</span>
              <ValidatedInput
                name="alphaDeg"
                type="number"
                value={alphaDeg || ''}
                onChange={(event) => setAlphaDeg(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <ActionButtonsRow>
              <button type="button" onClick={handleSave}>
                Salvar
              </button>
              <button type="button" onClick={() => document.getElementById('file-input').click()}>
                Carregar
              </button>
              <button type="submit" disabled={!isFormValid || isLoading || !canCalculate}>
                {isLoading ? (
                  <>
                    <Spinner size="16px" />
                    Calculando...
                  </>
                ) : !canCalculate ? (
                  'Limite atingido'
                ) : (
                  'Calcular'
                )}
              </button>
            </ActionButtonsRow>
            <input type="file" id="file-input" onChange={handleFileUpload} style={{ display: 'none' }} />
          </form>
        </Card>
        {result !== '' && (
          <>
            <Result $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay message="Recalculando..." />}
              <h2>Resultados</h2>
              <h3>
                Cálculo de V<sub>Rd2</sub>
              </h3>
              <MathDiv>
                <BlockMath math={`V_{Rd2}=${roundToDecimals(result.vrd2Response.vrd2, 2)} \\, \\text{kN}`} />
              </MathDiv>
              <h3>
                Cálculo de V<sub>c</sub>
              </h3>
              <MathDiv>
                {data.model == 1 && (
                  <>
                    <p>Modelo I</p>
                    <BlockMath math={`V_c=V_{c0}=${roundToDecimals(result.vcResponse.vc0, 2)} \\, \\text{kN}`} />
                  </>
                )}
                {data.model == 2 && data.vsd <= result.vcResponse.vc0 && (
                  <>
                    <p>Modelo II</p>
                    <BlockMath math={`V_{Sd} \\leq V_{c0}`} />
                    <BlockMath math={`V_c=V_{c0}=${roundToDecimals(result.vcResponse.vc, 2)} \\, \\text{kN}`} />
                  </>
                )}
                {data.model == 2 && data.vsd > result.vcResponse.vc0 && data.vsd <= result.vrd2Response.vrd2 && (
                  <>
                    <p>Modelo II</p>
                    <BlockMath math={`V_c= ${roundToDecimals(result.vcResponse.vc, 2)}\\, \\text{kN}`} />
                  </>
                )}
                {data.model == 2 && data.vsd > result.vrd2Response.vrd2 && (
                  <>
                    <p>Modelo II</p>
                    <BlockMath math={`V_{Sd} > V_{Rd2}`} />
                    <BlockMath math={`V_c= 0`} />
                  </>
                )}
              </MathDiv>
              <h3>Cálculo da armação</h3>
              <MathDiv>
                <BlockMath math={`A_{sw}=${roundToDecimals(result.aswResponse.asw, 2)}\\, \\text{cm}^2/\\text{m}`} />
              </MathDiv>
              <h3>Detalhamento</h3>
              <MathDiv>
                <InputDiv>
                  <p>Seleção da bitola</p>
                  <Select
                    id="rebarSelect"
                    value={selectedRebar}
                    onChange={(event) => setSelectedRebar(event.target.value)}
                  >
                    {result.aswRebars.map((rebar, index) => (
                      <option key={index} value={rebar.name}>
                        {rebar.name}
                      </option>
                    ))}
                  </Select>
                </InputDiv>
                <InputDiv>
                  <p>Número de ramos</p>
                  <RamosInput
                    id="sideSelect"
                    type="number"
                    min="1"
                    step="1"
                    value={sideNumber}
                    onChange={(event) => setSideNumber(event.target.value)}
                  />
                </InputDiv>
                <p>
                  {(() => {
                    const rawQuantity = result.aswRebars.find((r) => r.name === selectedRebar).quantity

                    const quantity = rawQuantity / Number(sideNumber || 1)

                    const raw = 100 / quantity
                    const spacing = Math.min(Math.floor(raw / 2.5) * 2.5, result.maxSpacingResponse.maxSpacing)

                    return `Será necessário ${selectedRebar} c/${spacing} cm`
                  })()}
                </p>
              </MathDiv>
            </Result>
            <Result className="results-section" $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay />}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <h2>Cálculos</h2>
                <CopyHTMLButton type="button" onClick={handleCopyResults}>
                  📋 Copiar Cálculos
                </CopyHTMLButton>
              </div>
              <h3>
                Cálculo de V<sub>Rd2</sub>
              </h3>
              <MathDiv>
                <DesktopOnlyDiv>
                  <BlockMath
                    math={`V_{Rd2}=0,54 \\cdot \\alpha \\cdot f_{cd} \\cdot \\left( cotg\\theta + cotg\\alpha \\right) \\cdot sen^2\\theta \\cdot b_w \\cdot d`}
                  />
                  <BlockMath
                    math={`V_{Rd2}=0,54 \\cdot \\left( 1 - \\dfrac{${data.fck}}{250} \\right) \\cdot \\dfrac{${data.fck / 10}}{1.4} \\cdot \\left( cotg\\ ${data.thetaDeg}\\degree + cotg\\ ${data.alphaDeg}\\degree \\right) \\cdot sen^2 ${data.thetaDeg}\\degree \\cdot ${data.b} \\cdot ${data.d}`}
                  />
                </DesktopOnlyDiv>
                <MobileOnlyDiv>
                  <BlockMath math={`V_{Rd2}=A \\cdot B \\cdot C`} />
                  <BlockMath math={`A=0,54 \\cdot \\alpha \\cdot f_{cd}`} />
                  <BlockMath math={`A=0,54 \\cdot \\left( 1 - \\dfrac{f_{ck}}{250} \\right) \\cdot f_{cd}`} />
                  <BlockMath
                    math={`A=0,54 \\cdot \\left( 1 - \\dfrac{${data.fck}}{250} \\right) \\cdot \\dfrac{${data.fck / 10}}{1.4}=${roundToDecimals(result.vrd2Response.aPart, 2)}`}
                  />
                  <BlockMath math={`B=cotg\\theta + cotg\\alpha`} />
                  <BlockMath
                    math={`B=cotg\\ ${data.thetaDeg}\\degree + cotg\\ ${data.alphaDeg}\\degree=${roundToDecimals(result.vrd2Response.bPart, 2)}`}
                  />
                  <BlockMath math={`C=sen^2\\theta \\cdot b_w \\cdot d`} />
                  <BlockMath
                    math={`C=sen^2 ${data.thetaDeg}\\degree \\cdot ${data.b} \\cdot ${data.d}=${roundToDecimals(result.vrd2Response.cPart, 2)}`}
                  />
                  <BlockMath
                    math={`V_{Rd2}=${roundToDecimals(result.vrd2Response.aPart, 2)} \\cdot ${roundToDecimals(result.vrd2Response.bPart, 2)} \\cdot ${roundToDecimals(result.vrd2Response.cPart, 2)}`}
                  />
                </MobileOnlyDiv>
                <BlockMath math={`\\boxed{V_{Rd2}=${roundToDecimals(result.vrd2Response.vrd2, 2)} \\, \\text{kN}}`} />
              </MathDiv>
              <h3>
                Cálculo de V<sub>c</sub>
              </h3>
              <MathDiv>
                <BlockMath math={`V_{c0}=0,09 \\cdot \\dfrac{{f_{ck}}^\\frac{2}{3}}{10} \\cdot b_w \\cdot d`} />
                <BlockMath
                  math={`V_{c0}=0,09 \\cdot \\dfrac{{${data.fck}}^\\frac{2}{3}}{10} \\cdot ${data.b} \\cdot ${data.d}`}
                />
                <BlockMath math={`V_{c0}=${roundToDecimals(result.vcResponse.vc0, 2)} \\, \\text{kN}`} />
                {data.model == 1 && (
                  <>
                    <p>Modelo I</p>
                    <BlockMath
                      math={`\\boxed{V_c=V_{c0}=${roundToDecimals(result.vcResponse.vc0, 2)} \\, \\text{kN}}`}
                    />
                  </>
                )}
                {data.model == 2 && data.vsd <= result.vcResponse.vc0 && (
                  <>
                    <p>Modelo II</p>
                    <BlockMath math={`V_{Sd} \\leq V_{c0}`} />
                    <BlockMath
                      math={`\\boxed{V_c=V_{c0}=${roundToDecimals(result.vcResponse.vc, 2)} \\, \\text{kN}}`}
                    />
                  </>
                )}
                {data.model == 2 && data.vsd > result.vcResponse.vc0 && data.vsd <= result.vrd2Response.vrd2 && (
                  <>
                    <p>Modelo II</p>
                    <BlockMath math={`V_{c0} < V_{Sd} \\leq V_{Rd2}`} />
                    <BlockMath math={`V_c= \\dfrac{V_{c0} \\cdot (V_{Rd2}-V_{Sd})}{V_{Rd2}-V_{c0}}`} />
                    <BlockMath
                      math={`V_c= \\dfrac{${roundToDecimals(result.vcResponse.vc0, 2)} \\cdot (${roundToDecimals(result.vrd2Response.vrd2, 2)} -${data.vsd})}{${roundToDecimals(result.vrd2Response.vrd2, 2)}-${roundToDecimals(result.vcResponse.vc0, 2)}}`}
                    />
                    <BlockMath math={`\\boxed{V_c= ${roundToDecimals(result.vcResponse.vc, 2)}\\, \\text{kN}}`} />
                  </>
                )}
                {data.model == 2 && data.vsd > result.vrd2Response.vrd2 && (
                  <>
                    <p>Modelo II</p>
                    <BlockMath math={`V_{Sd} > V_{Rd2}`} />
                    <BlockMath math={`\\boxed{V_c= 0}`} />
                  </>
                )}
              </MathDiv>
              <h3>Cálculo da armação</h3>
              <MathDiv>
                <DesktopOnlyDiv>
                  <BlockMath
                    math={`A_{sw,calc}=\\dfrac{111 \\cdot (V_{Sd}-V_c)}{(d \\cdot (cotg\\theta + cotg\\alpha)) \\cdot f_{yd} \\cdot sen^2\\alpha}`}
                  />
                  <BlockMath
                    math={`A_{sw,calc}=\\dfrac{111 \\cdot (${data.vsd}-${roundToDecimals(result.vcResponse.vc, 2)})}{${data.d} \\cdot (cotg${data.thetaDeg}\\degree + cotg${data.alphaDeg}\\degree) \\cdot ${roundToDecimals(result.aswResponse.fyd, 2)} \\cdot sen^2${data.alphaDeg}\\degree}`}
                  />
                </DesktopOnlyDiv>
                <MobileOnlyDiv>
                  <BlockMath
                    math={`A_{sw,calc}=\\dfrac{111 \\cdot (V_{Sd}-V_c)}{B \\cdot f_{yd} \\cdot sen^2\\alpha}`}
                  />
                  <BlockMath math={`B=d \\cdot (cotg\\theta + cotg\\alpha)`} />
                  <BlockMath
                    math={`B=${data.d} \\cdot (cotg${data.thetaDeg}\\degree + cotg${data.alphaDeg}\\degree)`}
                  />
                  <BlockMath math={`B=${roundToDecimals(result.aswResponse.bPart, 2)}`} />
                  <BlockMath
                    math={`A_{sw,calc}=\\dfrac{111 \\cdot (${data.vsd}-${roundToDecimals(result.vcResponse.vc, 2)})}{${roundToDecimals(result.aswResponse.bPart, 2)} \\cdot ${roundToDecimals(result.aswResponse.fyd, 2)} \\cdot sen^2${data.alphaDeg}\\degree}`}
                  />
                </MobileOnlyDiv>
                <BlockMath
                  math={`A_{sw,calc}=${roundToDecimals(result.aswResponse.aswc, 2)} \\, \\text{cm}^2/\\text{m}`}
                />
                <BlockMath math={`A_{sw,min}=\\rho_{min} \\cdot 100 \\cdot b_w`} />
                <BlockMath math={`A_{sw,min}=${result.aswResponse.roMin}\\% \\cdot 100 \\cdot ${data.b}`} />
                <BlockMath math={`A_{sw,min}=${result.aswResponse.aswMin}\\, \\text{cm}^2/\\text{m}`} />
                <BlockMath
                  math={`\\boxed{A_{sw}=${roundToDecimals(result.aswResponse.asw, 2)}\\, \\text{cm}^2/\\text{m}}`}
                />
              </MathDiv>
              <h3>Detalhamento</h3>
              <MathDiv>
                <InputDiv>
                  <p>Seleção da bitola</p>
                  <Select
                    id="rebarSelect"
                    value={selectedRebar}
                    onChange={(event) => setSelectedRebar(event.target.value)}
                  >
                    {result.aswRebars.map((rebar, index) => (
                      <option key={index} value={rebar.name}>
                        {rebar.name}
                      </option>
                    ))}
                  </Select>
                </InputDiv>
                <InputDiv>
                  <p>Número de ramos</p>
                  <RamosInput
                    id="sideSelect"
                    type="number"
                    min="1"
                    step="1"
                    value={sideNumber}
                    onChange={(event) => setSideNumber(event.target.value)}
                  />
                </InputDiv>
                {result.maxSpacingResponse.case == 1 && (
                  <>
                    <BlockMath math={`V_{Sd} \\leq V_{Rd2}`} />
                    <BlockMath math={`s_{máx} =0,6 \\cdot d \\leq 30 \\, \\text{cm}`} />
                    <BlockMath
                      math={`s_{máx} =${roundToDecimals(result.maxSpacingResponse.aPart, 2)} \\leq 30 \\, \\text{cm}`}
                    />
                    <BlockMath math={`s_{máx} =${result.maxSpacingResponse.maxSpacing} \\, \\text{cm}`} />
                  </>
                )}
                {result.maxSpacingResponse.case == 2 && (
                  <>
                    <BlockMath math={`V_{Sd} > V_{Rd2}`} />
                    <BlockMath math={`s_{máx} =0,3 \\cdot d \\leq 20 \\, \\text{cm}`} />
                    <BlockMath
                      math={`s_{máx} =${roundToDecimals(result.maxSpacingResponse.aPart, 2)} \\leq 20 \\, \\text{cm}`}
                    />
                    <BlockMath math={`s_{máx} =${result.maxSpacingResponse.maxSpacing} \\, \\text{cm}`} />
                  </>
                )}
                <p>
                  {(() => {
                    const rawQuantity = result.aswRebars.find((r) => r.name === selectedRebar).quantity

                    const quantity = rawQuantity / Number(sideNumber || 1)

                    const raw = 100 / quantity
                    const spacing = Math.min(Math.floor(raw / 2.5) * 2.5, result.maxSpacingResponse.maxSpacing)

                    return `Será necessário ${selectedRebar} c/${spacing} cm`
                  })()}
                </p>
              </MathDiv>
            </Result>
          </>
        )}
      </Container>
    </PageContainer>
  )
}
